import { useMemo } from 'react';
import { useLoadOrderHistory, useLoadRevenueHistory, useLoadCostPrice, useLoadMenus, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { filterOrdersByDateRange, filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import {
  calculateMenuEngineeringMatrix,
  getMenuEngineeringTimeWindow,
  calculateTimePeriods,
  calculateMenuCostVsActual,
  buildPeriodContexts,
  MenuCostVsActualData,
  MenuEngineeringDataPoint,
} from '../../../utils/chartsUtils';
import { enrichCostPriceListWithCurrentPrices } from '../../../utils/costPriceUtils';
import { useByRestaurantCostData } from '../../../hooks/useByRestaurantCostData';
import { filterOrdersByOverallType } from '../../orderhistory/utils';
import { filterRevenueByOverallType } from '../../revenuehistory/utils';
import { RevenueSourceType } from '../../../types/RevenueSourceType';
import { calculateTotalSpent } from '../../../utils/orderHistoryCalculations';
import { calculateTotalRevenue } from '../../../utils/revenueHistoryCalculations';
import { calculateCostPercentage, calculateCostPercentageChange, calculateAverageCostPercentage } from '../../../utils/kpiCalculations';
import { resolveCostTargets } from '../../../utils/costTargets';
import type { View } from '../../orderhistory/types';
import type { TimeViewOption } from '../../ui/TimeControls';

type OverallType = 'all' | 'food' | 'drinks';

export interface CostPercentData {
  isAllRestaurants: boolean;
  loading: boolean;
  overallTypeFilter: OverallType;
  selectedTimeOption: string;
  // time controls (passed straight through to TopSection)
  startDate: Date;
  endDate: Date;
  dateRange: { start: Date; end: Date } | null;
  setDateRange: (range: { start: Date; end: Date }) => void;
  setSelectedTimeOption: (v: string) => void;
  setOverallTypeFilter: (v: OverallType) => void;
  showExVatOrders: boolean;
  showExVatRevenue: boolean;
  // KPI values
  costPercentage: number;
  costPercentageChange: { changeValue: number; changeLabel: string } | null;
  avgCostPercentage: number;
  inefficiencyPp: number | null;
  inefficiencyEuro: number | null;
  // target cost% (per-tab, or revenue-weighted blend on the "all" tab)
  targetPct: number;
  // is enough of the menu costed to compute a meaningful theoretical line?
  hasEnoughCostSetup: boolean;
  // chart datasets
  menuCostVsActualData: MenuCostVsActualData[];
  menuEngineeringData: { data: MenuEngineeringDataPoint[]; hasEnoughData: boolean };
}

const COST_SETUP_COVERAGE_THRESHOLD = 0.2; // ≥20% of active items for the side must be costed

/**
 * Shared data wiring for the Cost Percent (Inslag) page. Mirrors the ChartsPage
 * calculation pipeline (so the actual-vs-theoretical numbers match the Charts
 * page exactly) and additionally derives the page's three KPIs, the dotted
 * target line, and the cost-setup coverage gate. ChartsPage is intentionally
 * left untouched — this is a parallel, page-specific composition.
 */
export function useCostPercentData(): CostPercentData {
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadMenus();
  useLoadSubrecipes();

  const orderHistoryStore = useOrderHistory();
  const revenueHistoryStore = useRevenueHistory();
  const costPriceStore = useCostPriceStore();
  const subrecipesById = useSubrecipesStore(s => s.subrecipesById);
  const isOrdersLoading = useOrderHistory(s => s.isLoading);
  const isRevenueLoading = useRevenueHistory(s => s.isLoading);
  const isCostPriceLoading = useCostPriceStore(s => s.isLoading);
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const isAllRestaurants = !!(selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant));
  const restaurantId = selectedRestaurant?.id;

  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter) as OverallType;
  const setOverallTypeFilterRaw = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);
  const reportSettingsMap = useRestaurantStore(s => s.reportSettings);

  const { startDate, endDate, selectedTimeOption, dateRange, setDateRange, setSelectedTimeOption } = useTimeControls();

  // Restaurant-filtered base data (mirrors DashboardPage: scopes all-restaurants
  // computations to the selected subset; empty filter = all restaurants).
  const revSourceToRestaurant = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const [rId, sources] of Object.entries(revenueSourcesMap)) {
      for (const source of sources as { id: string }[]) {
        lookup[source.id] = rId;
      }
    }
    return lookup;
  }, [revenueSourcesMap]);

  const baseOrders = useMemo(() => {
    if (!isAllRestaurants || restaurantFilters.length === 0) return orderHistoryStore.orderHistoryList;
    return orderHistoryStore.orderHistoryList.filter(order => {
      const rId = order.restaurantSection?.restaurantId || order.restaurantId;
      return rId && restaurantFilters.includes(rId);
    });
  }, [orderHistoryStore.orderHistoryList, restaurantFilters, isAllRestaurants]);

  const baseRevenue = useMemo(() => {
    if (!isAllRestaurants || restaurantFilters.length === 0) return revenueHistoryStore.revenueHistoryList;
    return revenueHistoryStore.revenueHistoryList.filter(rev => {
      const rId = rev.restaurantId || revSourceToRestaurant[rev.restaurantRevenueSourceId];
      return rId && restaurantFilters.includes(rId);
    });
  }, [revenueHistoryStore.revenueHistoryList, restaurantFilters, isAllRestaurants, revSourceToRestaurant]);

  // For time-based charts: all data up to endDate (so the trend can show history).
  const allDataUpToEndDateOrders = useMemo(() => {
    const earliest = baseOrders.length > 0
      ? new Date(Math.min(...baseOrders.map(o => new Date(o.orderDate).getTime())))
      : new Date(0);
    return filterOrdersByDateRange(baseOrders, earliest, endDate);
  }, [baseOrders, endDate]);

  const allDataUpToEndDateRevenue = useMemo(() => {
    const earliest = baseRevenue.length > 0
      ? new Date(Math.min(...baseRevenue.map(r => new Date(r.date).getTime())))
      : new Date(0);
    return filterRevenueByDateRange(baseRevenue, earliest, endDate);
  }, [baseRevenue, endDate]);

  // Selected-period data (startDate..endDate).
  const dateFilteredOrders = useMemo(() => filterOrdersByDateRange(baseOrders, startDate, endDate), [baseOrders, startDate, endDate]);
  const dateFilteredRevenue = useMemo(() => filterRevenueByDateRange(baseRevenue, startDate, endDate), [baseRevenue, startDate, endDate]);

  // Overall-type filtering (food/drinks/all).
  const filteredOrdersForTimeCharts = useMemo(() => filterOrdersByOverallType(allDataUpToEndDateOrders, overallTypeFilter), [allDataUpToEndDateOrders, overallTypeFilter]);
  const filteredRevenueForTimeCharts = useMemo(() => filterRevenueByOverallType(allDataUpToEndDateRevenue, overallTypeFilter), [allDataUpToEndDateRevenue, overallTypeFilter]);
  const filteredOrders = useMemo(() => filterOrdersByOverallType(dateFilteredOrders, overallTypeFilter), [dateFilteredOrders, overallTypeFilter]);
  const filteredRevenue = useMemo(() => filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter), [dateFilteredRevenue, overallTypeFilter]);

  // Map revenue item name → its type (food/drinks) for filtering cost analyses.
  const revenueItemTypeMap = useMemo(() => {
    const map = new Map<string, RevenueSourceType>();
    baseRevenue.forEach(revenue => {
      revenue.revenueData?.categories?.forEach(category => {
        category.items.forEach(item => {
          if (item.name && !map.has(item.name) && revenue.revenueSourceType) {
            map.set(item.name, revenue.revenueSourceType as RevenueSourceType);
          }
        });
      });
    });
    return map;
  }, [baseRevenue]);

  const byRestaurant = useByRestaurantCostData(costPriceStore.costPriceList, restaurantId);

  const enrichedCostPriceList = useMemo(() =>
    enrichCostPriceListWithCurrentPrices(costPriceStore.costPriceList, filteredOrdersForTimeCharts, {
      customIngredients: costPriceStore.customIngredients,
      showExVat: showExVatOrders,
      allSubrecipes: subrecipesById,
      revenueHistory: baseRevenue,
      byRestaurant,
      currentRestaurantId: restaurantId,
    }),
    [costPriceStore.costPriceList, filteredOrdersForTimeCharts, costPriceStore.customIngredients, showExVatOrders, subrecipesById, baseRevenue, byRestaurant, restaurantId]
  );

  // Filter both raw + enriched cost analyses by overall type (by menu item type).
  const filteredEnrichedCostPriceList = useMemo(() => {
    if (overallTypeFilter === 'all') return enrichedCostPriceList;
    const targetType = overallTypeFilter === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;
    return enrichedCostPriceList.filter(a => {
      if (!a.menuItemName) return false;
      return revenueItemTypeMap.get(a.menuItemName) === targetType;
    });
  }, [enrichedCostPriceList, revenueItemTypeMap, overallTypeFilter]);

  const filteredCostPriceList = useMemo(() => {
    if (overallTypeFilter === 'all') return costPriceStore.costPriceList;
    const targetType = overallTypeFilter === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;
    return costPriceStore.costPriceList.filter(a => {
      if (!a.menuItemName) return false;
      return revenueItemTypeMap.get(a.menuItemName) === targetType;
    });
  }, [costPriceStore.costPriceList, revenueItemTypeMap, overallTypeFilter]);

  const timePeriods = useMemo(() => calculateTimePeriods(endDate, selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all', 12), [endDate, selectedTimeOption]);

  const periodContexts = useMemo(() =>
    buildPeriodContexts(costPriceStore.costPriceList, allDataUpToEndDateOrders, allDataUpToEndDateRevenue, timePeriods, showExVatOrders, costPriceStore.customIngredients, byRestaurant, restaurantId, subrecipesById),
    [costPriceStore.costPriceList, allDataUpToEndDateOrders, allDataUpToEndDateRevenue, timePeriods, showExVatOrders, costPriceStore.customIngredients, byRestaurant, restaurantId, subrecipesById]
  );

  const menuCostVsActualData = useMemo(() =>
    calculateMenuCostVsActual(filteredCostPriceList, filteredOrdersForTimeCharts, filteredRevenueForTimeCharts, periodContexts, showExVatOrders, showExVatRevenue),
    [filteredCostPriceList, filteredOrdersForTimeCharts, filteredRevenueForTimeCharts, periodContexts, showExVatOrders, showExVatRevenue]
  );

  const menuEngineeringData = useMemo(() => {
    const { window: timeWindow, cutoffDate } = getMenuEngineeringTimeWindow(filteredRevenueForTimeCharts, endDate);
    const data = calculateMenuEngineeringMatrix(filteredEnrichedCostPriceList, filteredRevenueForTimeCharts, showExVatRevenue, endDate, cutoffDate || undefined);
    return { data, hasEnoughData: timeWindow !== null };
  }, [filteredEnrichedCostPriceList, filteredRevenueForTimeCharts, showExVatRevenue, endDate]);

  // ===== KPI derivations =====
  // Cost% for the selected period (dashboard semantics), with vs-prev delta.
  const costPercentage = useMemo(() => {
    const spend = calculateTotalSpent(filteredOrders, showExVatOrders);
    const revenue = calculateTotalRevenue(filteredRevenue, showExVatRevenue);
    return calculateCostPercentage(spend, revenue);
  }, [filteredOrders, filteredRevenue, showExVatOrders, showExVatRevenue]);

  const costPercentageChange = useMemo(() =>
    calculateCostPercentageChange(costPercentage, selectedTimeOption as View, startDate, endDate, filterOrdersByOverallType(baseOrders, overallTypeFilter), filterRevenueByOverallType(baseRevenue, overallTypeFilter), showExVatOrders, showExVatRevenue),
    [costPercentage, selectedTimeOption, startDate, endDate, baseOrders, baseRevenue, overallTypeFilter, showExVatOrders, showExVatRevenue]
  );

  // Avg cost% across ALL historical buckets (not the selected period), so it
  // stays stable as you navigate periods — same behavior as the Revenue page's
  // average KPI. Uses the overall-type-filtered full history.
  const avgCostPercentage = useMemo(() =>
    calculateAverageCostPercentage(
      filterOrdersByOverallType(baseOrders, overallTypeFilter),
      filterRevenueByOverallType(baseRevenue, overallTypeFilter),
      showExVatOrders,
      showExVatRevenue,
      selectedTimeOption as TimeViewOption
    ),
    [baseOrders, baseRevenue, overallTypeFilter, showExVatOrders, showExVatRevenue, selectedTimeOption]
  );

  // Inefficiency for the selected period = last bucket of the trend.
  const { inefficiencyPp, inefficiencyEuro } = useMemo(() => {
    const last = menuCostVsActualData[menuCostVsActualData.length - 1];
    if (!last || last.inefficiency === null) return { inefficiencyPp: null, inefficiencyEuro: null };
    return { inefficiencyPp: last.inefficiency, inefficiencyEuro: (last.inefficiency / 100) * last.totalRevenue };
  }, [menuCostVsActualData]);

  // Target cost% — per-tab, or revenue-weighted blend across food/drinks/other on "all".
  const targetPct = useMemo(() => {
    const targets = resolveCostTargets(restaurantId ? reportSettingsMap[restaurantId]?.costTargets : undefined);
    if (overallTypeFilter === 'food') return targets.food;
    if (overallTypeFilter === 'drinks') return targets.drinks;
    const foodRev = calculateTotalRevenue(filterRevenueByOverallType(dateFilteredRevenue, 'food'), showExVatRevenue);
    const drinksRev = calculateTotalRevenue(filterRevenueByOverallType(dateFilteredRevenue, 'drinks'), showExVatRevenue);
    const totalRev = calculateTotalRevenue(dateFilteredRevenue, showExVatRevenue);
    const otherRev = Math.max(0, totalRev - foodRev - drinksRev);
    if (totalRev <= 0) return targets.overall;
    return (targets.food * foodRev + targets.drinks * drinksRev + targets.overall * otherRev) / totalRev;
  }, [restaurantId, reportSettingsMap, overallTypeFilter, dateFilteredRevenue, showExVatRevenue]);

  // Cost-setup coverage gate: ≥20% of active items (sold in period) for the
  // relevant side are costed (have a cost analysis with ingredients). Side =
  // drinks when the drinks tab is active; food otherwise (food + all).
  const hasEnoughCostSetup = useMemo(() => {
    const side = overallTypeFilter === 'drinks' ? RevenueSourceType.DRINKS : RevenueSourceType.FOOD;
    const activeNames = new Set<string>();
    dateFilteredRevenue.forEach(rev => {
      if ((rev.revenueSourceType as RevenueSourceType) !== side) return;
      rev.revenueData?.categories?.forEach(c => c.items.forEach(i => { if (i.name) activeNames.add(i.name); }));
    });
    if (activeNames.size === 0) return false;
    const costedNames = new Set(
      costPriceStore.costPriceList
        .filter(a => a.menuItemName && a.ingredients && a.ingredients.length > 0)
        .map(a => a.menuItemName as string)
    );
    let costed = 0;
    activeNames.forEach(n => { if (costedNames.has(n)) costed++; });
    return costed / activeNames.size >= COST_SETUP_COVERAGE_THRESHOLD;
  }, [overallTypeFilter, dateFilteredRevenue, costPriceStore.costPriceList]);

  return {
    isAllRestaurants,
    loading: isOrdersLoading || isRevenueLoading || isCostPriceLoading,
    overallTypeFilter,
    selectedTimeOption,
    startDate,
    endDate,
    dateRange,
    setDateRange,
    setSelectedTimeOption: setSelectedTimeOption as (v: string) => void,
    setOverallTypeFilter: setOverallTypeFilterRaw as (v: OverallType) => void,
    showExVatOrders,
    showExVatRevenue,
    costPercentage,
    costPercentageChange,
    avgCostPercentage,
    inefficiencyPp,
    inefficiencyEuro,
    targetPct,
    hasEnoughCostSetup,
    menuCostVsActualData,
    menuEngineeringData,
  };
}
